import type { EditorialState } from './types';
import type { PostAction } from './states';

/**
 * Strings more than one editorial surface renders — the state vocabulary, the
 * transition verbs, and the editor the two authoring routes share. A string one
 * page owns lives in that page's own `copy.ts`.
 *
 * The copy addresses the operator: no design rationale, no technical
 * vocabulary, and no restatement of a mechanic the screen already performs.
 */

export const stateLabels: Readonly<Record<EditorialState, string>> = {
  DRAFT: 'مسوّدة',
  AWAITING_REVIEW: 'بانتظار المراجعة',
  PUBLISHED: 'منشور',
  REJECTED: 'مردود',
};

/** The row menu's verbs, in the pack's wording. */
export const actionLabels: Readonly<Record<PostAction, string>> = {
  open: 'افتح المقال',
  edit: 'حرّر المقال',
  submit: 'ارفع للمراجعة',
  approve: 'وافِق وانشر',
  reject: 'ردّ المقال',
  /**
   * The label on the recall path — awaiting review, where nothing public has
   * happened yet. The published path takes `unpublishCopy.menu` instead: one
   * endpoint, two acts, and a label that named only the destination would have
   * an operator take a live article off the site while reading the word
   * «مسوّدة».
   */
  withdraw: 'أعِده مسوّدةً',
  delete: 'احذف المقال',
};

/**
 * Withdrawing a **published** post — the same `:withdraw` transition, named for
 * what it does rather than for where it lands.
 *
 * It is confirmed, and the deletion dialog is not the model for why: this is
 * fully reversible — the post is re-submitted and re-approved — but it is the
 * only action in this console that removes something from the public site the
 * moment it is pressed, and it sits in a menu one entry away from «احذف
 * المقال». The dialog states the effect and the way back, and its intent is
 * `warning` rather than `danger`, because the post is not being destroyed.
 */
export const unpublishCopy = {
  menu: 'ألغِ النشر',
  heading: (title: string) => `إلغاء نشر «${title}»`,
  body: 'يُرفع المقال من الموقع العامّ فوراً ويعود مسوّدةً قابلةً للتحرير. لا يُحذف شيء، ولا يظهر للقرّاء حتى يُرفع للمراجعة ويوافق عليه مراجعٌ من جديد — النشر بيدين اثنتين في كلّ مرّة.',
  commit: 'ألغِ النشر',
} as const;

/** The rejection dialog, opened from the list and from the post alike. */
export const rejectCopy = {
  heading: 'ردّ المقال إلى كاتبه',
  body: 'يعود المقال إلى كاتبه قابلاً للتحرير، ومعه سببك. لا يظهر شيء من هذا للقرّاء — المقال لم يُنشر بعد.',
  bodyNamed: (author: string) =>
    `يعود المقال إلى ${author} قابلاً للتحرير، ومعه سببك. لا يظهر شيء من هذا للقرّاء — المقال لم يُنشر بعد.`,
  label: 'سبب الردّ',
  placeholder: 'ما الذي يحتاج تغييراً قبل النشر؟',
  hint: 'يقرأه كاتب المقال وحده.',
  required: 'اكتب سبب الردّ قبل الإرسال.',
  commit: 'ردّ مع السبب',
} as const;

/**
 * The delete confirmation. It states the finality, because the act is final.
 *
 * The earlier copy promised a «محذوف» state the post would sit in and a
 * restore one press away. Both are gone: the dialog is now the only thing
 * standing between the operator and a destroyed article, so it names the post,
 * names what goes with it, and says plainly that nothing comes back.
 */
export const deleteCopy = {
  heading: (title: string) => `حذف مقال «${title}»؟`,
  body: 'يُحذف المقال نهائياً: يُرفع من القرّاء، ويخرج من هذه القائمة، وتذهب معه تعليقاته وتفاعلاته وسجلّ مراجعته. لا يمكن التراجع عن هذا الإجراء.',
  commit: 'احذف نهائياً',
} as const;

/** The canvas and rail both authoring routes share. */
export const editorCopy = {
  bodyLabel: 'جسم المقال',
  titleLabel: 'عنوان المقال (مطلوب)',
  titlePlaceholder: 'عنوان المقال…',
  paragraphPlaceholder: 'اكتب هنا، أو «/» لإدراج كتلة…',
  canvasHeading: 'نصّ المقال',
  flowHelp:
    'اكتب مباشرةً في المساحة أعلاه. مفتاح Enter يبدأ فقرةً جديدة، وكتابة «/» في سطر فارغ تفتح قائمة الإدراج. الصورة والمقطع يدخلان بطاقتين داخل النصّ لا يمسّهما التحرير.',
  slashLabel: 'أدرج كتلة',
  addRowLabel: 'أضف كتلة في آخر الجسم',
  addRowLead: 'أضف في الآخر:',
  moveUp: (kind: string) => `انقل ${kind} إلى الأعلى`,
  moveDown: (kind: string) => `انقل ${kind} إلى الأسفل`,
  removeBlock: (kind: string) => `احذف ${kind}`,
  captionLabel: (kind: string) => `تعليق ${kind}`,
  mediaReplace: 'استبدل',

  railLabel: 'ضبط المقال',
  requiredNote: 'الحقول المعلَّمة بـ * مطلوبة.',

  publish: {
    heading: 'النشر',
    stateLabel: 'الحالة',
  },

  taxonomy: {
    heading: 'الوسوم والكاتب',
    authorLabel: 'الكاتب',
    authorHint:
      'المقال يُنسب إلى حسابك، وهذا ما تقرؤه الخدمة من جلستك. الكاتب ليس حقلاً يُختار: مراجعة الاثنين تقوم على أنّ الكاتب هو صاحب الطلب.',
    tagsLabel: 'الوسوم',
    tagsSearch: 'ابحث في الوسوم',
    tagsPlaceholder: 'ابحث في الوسوم…',
    tagsCount: (count: string) => `${count} من المفردة`,
    tagsNoResults: 'لا وسم بهذا الاسم في المفردة.',
    tagsRemove: (label: string) => `أزل وسم «${label}»`,
    tagsHint: 'من مفردة الوسوم القائمة. الوسم الجديد يُنشأ في إدارة الوسوم ثمّ يظهر هنا.',
    tagsLink: 'إدارة الوسوم',
  },

  art: {
    heading: 'الصور',
    lead: 'المصغّرة وحدها مطلوبة، واللوحة تُشتقّ منها بقصٍّ متناسب ما لم ترفع لها صورةً خاصّة.',
    thumbLabel: 'المصغّرة',
    /**
     * **Read from `GET /uploadPolicy?subjectType=EDITORIAL_POST`, never written
     * down.** This line used to say «المقاس الموصى به 1280×720», and 1280×720 is
     * refused: the 16:9 floor `assets` enforces is 1920×1080. An operator who
     * followed the console's own guidance to the pixel met a refusal, so the
     * number that reaches them is now the number the service checks.
     */
    thumbHint: (formats: string, maxMb: string, minWidth: string, minHeight: string) =>
      `الغرض: بطاقة الشبكة، بنسبة ١٦:٩. ${formats} — حتّى ${maxMb} ميغابايت، ولا تقلّ عن ${minWidth}×${minHeight} بكسل.`,
    heroLabel: 'اللوحة',
    /** Derived from the thumbnail, so it is measured against the same floor. */
    heroHint: (formats: string, maxMb: string, minWidth: string, minHeight: string) =>
      `مقتطعة من المصغّرة تلقائياً. ارفع صورةً خاصّة متى أردت قصّاً غير الذي يختاره النظام — ${formats}، حتّى ${maxMb} ميغابايت، ولا تقلّ عن ${minWidth}×${minHeight} بكسل.`,
    /**
     * The refusal the console makes itself, before any request leaves.
     *
     * The geometry is already read in the browser to declare it on the
     * registration, so a file below the floor is knowable without a round trip —
     * and the sentence names BOTH numbers, because «الصورة صغيرة» leaves the
     * operator to guess which of the two dimensions to fix and by how much.
     */
    tooSmall: (width: string, height: string, minWidth: string, minHeight: string) =>
      `الصورة ${width}×${height} بكسل، والحدّ الأدنى ${minWidth}×${minHeight}. اختر صورةً أكبر — لا تُكبَّر الصغيرة.`,
    /** The chosen file is not an image this console can measure. */
    unreadable: 'تعذّرت قراءة أبعاد هذا الملفّ. اختر صورةً بصيغة مقبولة.',
    upload: 'ارفع صورة',
    /** A disabled control explains itself in adjacent text — always. */
    uploading: 'يجري الرفع…',
    /** Said on the create route, where there is no subject to register artwork against yet. */
    uploadAfterSave: 'احفظ المسوّدة أوّلاً، ثمّ ارفع الصورة من صفحة تحرير المقال.',
  },

  related: {
    heading: 'عناوين ذات صلة',
    lead: 'تظهر رفّاً أسفل المقال. الإرفاق إشارةٌ إلى العنوان لا نسخةٌ منه: حذف المقال لا يمسّه، وتغيّر حالته يظهر في الرفّ من تلقائه.',
    listLabel: 'العناوين المرفقة بهذا المقال',
    empty: 'لم يُرفَق عنوانٌ بعد.',
    search: 'ابحث في الفهرس بالاسم',
    placeholder: 'ابحث بالاسم…',
    count: (count: string) => `${count} من الفهرس`,
    noResults: 'لا عنوان بهذا الاسم.',
    remove: (label: string) => `أزل «${label}» من العناوين المرفقة`,
    hint: 'ابحث في الفهرس بالاسم. يمكنك إرفاق عنوانٍ لم يُعرَض بعد — يظهر في رفّ المقال يوم يصير متاحاً للمشاهدة.',
  },

  actions: {
    saveDraft: 'احفظ مسوّدة',
    saveChanges: 'احفظ التغييرات',
    submit: 'ارفع للمراجعة',
    cancel: 'ألغِ',
    delete: 'احذف المقال',
  },

  /** The one state the two authoring routes may meet and cannot write into. */
  uneditableBody:
    'جسم هذا المقال محفوظٌ بشكلٍ لا يقرأه هذا المحرّر، فلن يُعرض هنا ولن يُحفَظ فوقه. افتح المقال للقراءة، وأبلغ الدعم.',

  /**
   * Shown when leaving the editor would discard what was typed.
   *
   * Three fields rather than one sentence, because the console's own
   * `ConfirmDialog` draws a heading, a body and a commit label as three separate
   * things — and the commit label is what states the consequence on the button
   * the operator is about to press. `window.confirm` could carry only the middle
   * one and labelled its two buttons in the browser's language, not this
   * surface's.
   */
  unsaved: {
    heading: 'مغادرة بلا حفظ؟',
    body: 'لديك تغييرات لم تُحفظ في هذا المقال. إن غادرت الآن فستضيع.',
    commitLabel: 'غادر بلا حفظ',
    intent: 'warning',
  },

  validation: {
    titleRequired: 'اكتب عنوان المقال.',
    titleTooLong: 'العنوان أطول ممّا يقبله الحقل. اختصره.',
    authorRequired: 'اختر كاتب المقال.',
  },
} as const;
